import { FeatureFlagEnvironmentDetailDto } from '@flagsync/skyline';
import * as Sentry from '@sentry/react';
import {
  FlaskConical,
  LucideIcon,
  Sparkles,
  Terminal,
  TextCursor,
  TrendingUp,
  Users,
} from 'lucide-react';

import { TypeScriptIcon } from '@/components/svgs/icons';

import { Hero } from './types';

export const experimentFeature: Hero = {
  tagline: 'Optimize user engagement',
  heading: 'AI-Powered Experiments',
  description:
    'Let FlagSync’s AI spark test ideas, optimize features, and drive decisions with real user data.',
  features: [
    {
      key: 'improve',
      name: 'A/B/n testing, simplified',
      description:
        'Continuously refine your product with ongoing experiments. Stay ahead of user needs and market shifts.',
      icon: FlaskConical,
    },
    {
      key: 'suggestion',
      name: 'Smart Test Suggestions',
      description:
        'Our AI analyzes your site to recommend experiments tailored to your app.',
      icon: Sparkles,
    },
    {
      key: 'optimize',
      name: 'Optimize User Experience',
      description: 'Test different flows or designs to find what users love.',
      icon: TrendingUp,
    },
  ],
};

export const cliFeature: Hero = {
  tagline: 'Type-safety',
  heading: 'Type-Safe Feature Flags',
  description:
    "FlagSync's CLI generates TypeScript types from your feature flag config. Get autocompletion, compile-time safety, and confidence your code matches your flags.",
  features: [
    {
      key: 'cli',
      name: 'Stay in the flow',
      description:
        'Instantly sync types from your terminal—no breaking your rhythm.',
      icon: Terminal,
    },
    {
      key: 'types',
      name: 'Never mistype a flag',
      description:
        'Eliminate invalid or outdated flag keys with generated TypeScript types.',
      icon: TypeScriptIcon as LucideIcon,
    },
    {
      key: 'usage',
      name: 'Code with confidence',
      description:
        'Enjoy full autocompletion and static safety. No memorizing flags or checking dashboards.',
      icon: TextCursor,
    },
  ],
};

export const whyFlagSyncFeature: Hero = {
  tagline: 'Feature flags for all',
  heading: 'Why FlagSync?',
  description: 'Launch smarter, scale affordably',
  features: [
    {
      key: 'insights',
      name: 'Actionable analytics, out of the box',
      description:
        'Track live events, flag impressions, and experiment results with clean, readable dashboards—no data science degree required.',
      icon: TrendingUp,
    },
    {
      key: 'setup',
      name: 'Confidence through TypeScript',
      description:
        "FlagSync's CLI generates TypeScript types from your feature flags. Get autocompletion and compile-time safety.",
      icon: TypeScriptIcon as LucideIcon,
    },
    {
      key: 'rollout',
      name: 'Smarter rollouts, fewer fires',
      description:
        'Target user segments or release gradually by percentage to catch bugs before they hit everyone.',
      icon: Users,
    },
  ],
};

export const customAttributes = [
  {
    attributeId: 1,
    name: 'Email',
    organizationId: 1,
    workspaceId: 1,
  },
  {
    attributeId: 2,
    name: 'User Group',
    organizationId: 1,
    workspaceId: 1,
  },
  {
    attributeId: 4,
    name: 'Job Title',
    organizationId: 1,
    workspaceId: 1,
  },
  {
    attributeId: 5,
    name: 'Region',
    organizationId: 1,
    workspaceId: 1,
  },
  {
    attributeId: 6,
    name: 'App Version',
    organizationId: 1,
    workspaceId: 1,
  },
];

export const fakeFfes: FeatureFlagEnvironmentDetailDto = {
  organizationId: 1,
  workspaceId: 1,
  environmentId: 1,
  featureFlag: {
    featureFlagId: 1,
    publicFeatureFlagId: '0x123',
    name: 'Register Copy',
    key: 'register_copy',
    description: 'Controls the display text for registration button',
    createdAt: '2025-07-15T22:52:00Z',
    updatedAt: '2025-07-15T22:52:00Z',
    variants: [
      {
        variantId: 1,
        dataTypeCode: 'BOOL',
        name: 'Enabled',
        description: 'Enable the feature',
        createdAt: '2025-07-15T22:52:00Z',
        updatedAt: '2025-07-15T22:52:00Z',
        jsonValue: {},
        numberValue: 0,
        stringValue: '',
        booleanValue: true,
      },
      {
        variantId: 2,
        dataTypeCode: 'STR',
        name: 'Disabled',
        description: 'Disable the feature',
        createdAt: '2025-07-15T22:52:00Z',
        updatedAt: '2025-07-15T22:52:00Z',
        jsonValue: {},
        numberValue: 0,
        stringValue: '',
        booleanValue: false,
      },
    ],
  },
  state: {
    featureFlagEnvironmentStateId: 1,
    environmentId: 1,
    isEnabled: true,
    createdAt: '2025-07-15T22:52:00Z',
    updatedAt: '2025-07-15T22:52:00Z',
    enabledSingleVariantId: 0,
    disabledSingleVariantId: 0,
    seed: 'register_copy_seed',
    percentageRollout: [
      {
        variantId: 1,
        percentage: 25,
      },
      {
        variantId: 2,
        percentage: 75,
      },
    ],
    targetingRules: [
      {
        variantEnvironmentTargetingRuleId: 24,
        variantId: 1,
        name: 'Enable for QA',
        order: 0,
        createdAt: '2025-04-06T15:13:01.470Z',
        updatedAt: '2025-04-06T15:13:01.470Z',
        clauses: [
          {
            order: 0,
            operator: 'IOO',
            attribute: 'CUSTOM',
            values: [
              {
                targetingRuleClauseValueId: 38,
                value: 'QA Testers',
              },
            ],
            customAttribute: {
              attributeId: 2,
              name: 'User Group',
            },
            targetingRuleClauseId: 25,
            createdAt: '2025-04-06T15:13:01.470Z',
            updatedAt: '2025-04-06T15:13:01.470Z',
          },
        ],
      },
      {
        variantEnvironmentTargetingRuleId: 23,
        variantId: 2,
        name: 'Disable for App Version',
        order: 0,
        createdAt: '2025-04-06T15:06:58.872Z',
        updatedAt: '2025-04-06T15:12:21.126Z',
        clauses: [
          {
            order: 1,
            operator: 'SVLTE',
            attribute: 'CUSTOM',
            values: [
              {
                targetingRuleClauseValueId: 37,
                value: '2.3.19',
              },
            ],
            customAttribute: {
              attributeId: 6,
              name: 'App Version',
            },
            targetingRuleClauseId: 24,
            createdAt: '2025-04-06T15:07:14.081Z',
            updatedAt: '2025-04-06T15:12:21.126Z',
          },
        ],
      },
    ],
  },
  experiments: [],
};

export const generateIntervals = () => {
  const intervals = [];
  const today = new Date();
  const countValues = [
    480, 465, 405, 460, 425, 420, 460, 430, 460, 460, 396, 460, 431, 460, 437,
    430, 403, 405, 447, 442, 417, 447, 405, 419, 434, 361, 389, 378, 325, 360,
    377, 337, 374, 324, 352, 306, 322, 249, 295, 284, 277, 249, 269, 228, 223,
    247, 177, 211, 135, 149, 201, 116, 148, 156, 151, 139, 58, 111, 54, 54,
  ];

  for (let i = 0; i < countValues.length; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const formattedDate = date.toISOString().split('T')[0];
    intervals.push({ interval: formattedDate, count: countValues[i] });
  }

  return intervals;
};

export const generateExperimentData = (): {
  id: string;
  variantName: string;
  data: {
    y: number;
    x: string;
  }[];
}[] => {
  const now = new Date();
  const yValues = {
    Register: [
      0, 0, 0, 1, 1, 4, 4, 5, 6, 7, 7, 8, 9, 11, 12, 14, 14, 14, 17, 17, 17, 18,
      19, 20, 20, 21, 21, 22, 22, 22, 22, 22, 23, 24, 26, 26, 27, 27, 27, 28,
      29, 29, 30,
    ],
    'Sign Up': [
      0, 1, 3, 5, 7, 9, 9, 9, 9, 9, 11, 12, 13, 13, 14, 15, 18, 18, 20, 21, 23,
      23, 24, 25, 27, 27, 28, 28, 29, 30, 34, 35, 35, 36, 37, 40, 41, 42, 42,
      43, 44, 44,
    ],
    'Join Now': [
      0, 0, 1, 3, 3, 7, 8, 10, 11, 11, 13, 15, 16, 18, 18, 20, 20, 20, 20, 20,
      21, 21, 24, 27, 29, 31, 32, 34, 36, 37, 37, 39, 39, 39, 39, 43, 43, 44,
      45, 46, 48, 50,
    ],
  };

  const formatDateTime = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const generateVariantData = (yValues: number[]) => {
    return yValues.map((y, index) => {
      const date = new Date(now);
      date.setHours(now.getHours() - (yValues.length - 1) + index);
      return { y, x: formatDateTime(date) };
    });
  };

  return [
    {
      id: '1',
      variantName: 'Register',
      data: generateVariantData(yValues['Register']),
    },
    {
      id: '2',
      variantName: 'Sign Up',
      data: generateVariantData(yValues['Sign Up']),
    },
    {
      id: '3',
      variantName: 'Join Now',
      data: generateVariantData(yValues['Join Now']),
    },
  ];
};
export const variants = [
  { variantId: 1, count: 208, name: 'Register' },
  { variantId: 2, count: 1581, name: 'Sign Up' },
  { variantId: 3, count: 17495, name: 'Join Now' },
];

let data: {
  id: string;
  variantName: string;
  data: {
    y: number;
    x: string;
  }[];
}[] = [
  {
    id: '1',
    variantName: 'Register',
    data: [],
  },
  {
    id: '2',
    variantName: 'Sign Up',
    data: [],
  },
  {
    id: '3',
    variantName: 'Join Now',
    data: [],
  },
];

try {
  data = generateExperimentData();
} catch (e) {
  Sentry.captureException(e);
}

export const fakeExperiment = {
  featureFlagId: 1,
  publicFeatureFlagId: '0x1',
  featureFlagName: 'Register Copy',
  isFlagEnabled: true,
  chartData: {
    metric: {
      isPrimaryMetric: true,
      metricId: 3,
      createdAt: '2024-05-30T03:09:30.958Z',
      updatedAt: '2024-05-30T03:09:30.958Z',
      organizationId: 1,
      workspaceId: 1,
      name: 'Register Conversion',
      eventKey: 'register-event',
      valueField: 'eventValue',
      description:
        'We believe that updating the "Register" button\'s copy could lead to an increase in user registrations.',
      measurementType: 'pct',
      successCriteria: 'inc',
    },
    data: data,
  },
  metricBatch: {
    payload: [
      {
        variantId: 1,
        min: 0,
        max: 0,
        mean: 0,
        sum: 189,
        count: 30,
        rawCount: 30,
        rawSum: 189,
      },
      {
        variantId: 2,
        min: 0,
        max: 0,
        mean: 0,
        sum: 184,
        count: 44,
        rawCount: 44,
        rawSum: 184,
      },
      {
        variantId: 3,
        min: 0,
        max: 0,
        mean: 0,
        sum: 171,
        count: 50,
        rawCount: 50,
        rawSum: 171,
      },
    ],
    aggregateId: 4968,
    batchId: 'xLTEsYxtMF_C',
    metricId: 3,
    eventKey: 'register-event',
    isPrimaryMetric: true,
    createdAt: '2025-01-09T03:59:47.160Z',
  },
  percentageRollout: [
    { variantId: 1, percentage: 33 },
    { variantId: 2, percentage: 33 },
    { variantId: 3, percentage: 34 },
  ],
  baselineVariantId: 1,
  variants: [
    {
      variantId: 1,
      dataTypeCode: 'STR',
      name: 'Register',
      description: '',
      createdAt: '2024-05-27T05:01:20.917Z',
      updatedAt: '2024-05-27T05:01:20.917Z',
      jsonValue: null,
      numberValue: null,
      booleanValue: null,
      stringValue: 'Register',
    },
    {
      variantId: 2,
      dataTypeCode: 'STR',
      name: 'Sign Up',
      description: '',
      createdAt: '2024-05-27T05:01:20.917Z',
      updatedAt: '2024-05-27T05:01:20.917Z',
      jsonValue: null,
      numberValue: null,
      booleanValue: null,
      stringValue: 'Sign Up',
    },
    {
      variantId: 3,
      dataTypeCode: 'STR',
      name: 'Join Now',
      description: '',
      createdAt: '2024-05-27T05:01:20.917Z',
      updatedAt: '2024-05-27T05:01:20.917Z',
      jsonValue: null,
      numberValue: null,
      booleanValue: null,
      stringValue: 'Join Now',
    },
  ],
  metric: {
    isPrimaryMetric: true,
    metricId: 3,
    createdAt: '2024-05-30T03:09:30.958Z',
    updatedAt: '2024-05-30T03:09:30.958Z',
    organizationId: 1,
    workspaceId: 1,
    name: 'Register Conversion',
    eventKey: 'register-event',
    valueField: 'eventValue',
    description:
      'Updating the "Register" button could lead to an increase in user registrations.',
    measurementType: 'pct',
    successCriteria: 'inc',
  },
};
